import {
  TenantMember,
  TenantMemberList,
  TenantMemberRole,
  TenantVersion,
} from '@/lib/api/generated/data-contracts';
import { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

declare module 'axios' {
  interface InternalAxiosRequestConfig {
    localTenantId?: string;
  }
}

export const LOCAL_SESSIONS_STORAGE_KEY = 'hatchet.localSessions';
export const LOCAL_SESSION_HEADER = 'X-Hatchet-Local-Session';
const MEMBERSHIPS_PATH = '/api/v1/users/memberships';
let localSessionRedirectStarted = false;

export interface LocalSession {
  tenantId: string;
  port: number;
  project: string;
  env: string;
  secret: string;
}

export type LocalSessions = Record<string, LocalSession>;

export function readLocalSessions(): LocalSessions {
  try {
    const raw = localStorage.getItem(LOCAL_SESSIONS_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as LocalSessions) : {};
  } catch {
    return {};
  }
}

function writeLocalSessions(sessions: LocalSessions) {
  localStorage.setItem(LOCAL_SESSIONS_STORAGE_KEY, JSON.stringify(sessions));
}

export function writeLocalSession(
  tenantId: string,
  port: number,
  project: string,
  env: string,
  secret: string,
) {
  const sessions = readLocalSessions();
  sessions[tenantId] = { tenantId, port, project, env, secret };
  writeLocalSessions(sessions);
}

export function forgetLocalSession(tenantId: string) {
  const sessions = readLocalSessions();
  delete sessions[tenantId];
  writeLocalSessions(sessions);
}

export function localSessionName(session: LocalSession) {
  return `${session.project} · ${session.env} · local`;
}

export function localSessionBaseUrl(session: LocalSession) {
  return `http://127.0.0.1:${session.port}`;
}

export function localSessionRequestInterceptor(
  config: InternalAxiosRequestConfig,
  tenantId: string | null,
) {
  const resources = config.xResources ?? [];
  if (!tenantId || !resources.includes('tenant')) {
    return config;
  }
  const session = readLocalSessions()[tenantId];
  if (!session) {
    return config;
  }
  config.baseURL = localSessionBaseUrl(session);
  config.withCredentials = false;
  config.localTenantId = tenantId;
  config.headers.set(LOCAL_SESSION_HEADER, session.secret);
  config.headers.delete('Authorization');
  return config;
}

function localMembership(session: LocalSession): TenantMember {
  const stamp = new Date(0).toISOString();
  const metadata = { id: session.tenantId, createdAt: stamp, updatedAt: stamp };
  return {
    metadata,
    user: { email: 'local@hatchet.dev', name: 'hatchet-toolkit dev' },
    role: TenantMemberRole.OWNER,
    tenant: {
      metadata,
      name: localSessionName(session),
      slug: `${session.project}-${session.env}-local`,
      version: TenantVersion.V1,
    },
  };
}

export function localSessionMembershipsInterceptor(response: AxiosResponse) {
  if (response.config.url !== MEMBERSHIPS_PATH) {
    return response;
  }
  const sessions = Object.values(readLocalSessions());
  if (sessions.length === 0) {
    return response;
  }
  const list = response.data as TenantMemberList;
  list.rows = [...(list.rows ?? []), ...sessions.map(localMembership)];
  return response;
}

export function localSessionErrorInterceptor(error: AxiosError) {
  const tenantId = error.config?.localTenantId;
  if (!tenantId || error.code !== AxiosError.ERR_NETWORK) {
    return Promise.reject(error);
  }
  const tenantPath = `/tenants/${tenantId}`;
  const pathname = window.location.pathname;
  if (pathname !== tenantPath && !pathname.startsWith(`${tenantPath}/`)) {
    return Promise.reject(error);
  }
  if (localSessionRedirectStarted || !readLocalSessions()[tenantId]) {
    return Promise.reject(error);
  }
  localSessionRedirectStarted = true;
  window.location.replace(`/local#tenant=${tenantId}`);
  return Promise.reject(error);
}
